import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client.js';

interface TotpStatus {
  totp_enabled: boolean;
  google_linked: boolean;
  role: string;
}

export function SettingsPage(): JSX.Element {
  return (
    <div className="max-w-2xl space-y-8">
      <header>
        <h1 className="text-2xl font-semibold">Account settings</h1>
        <p className="text-sm text-ink-muted">Manage your password, two-factor authentication, and Google account link.</p>
      </header>
      <ChangePasswordCard />
      <GoogleLinkCard />
      <GoogleCalendarCard />
      <TwoFactorCard />
    </div>
  );
}

function GoogleLinkCard(): JSX.Element {
  const { data } = useQuery<TotpStatus>({
    queryKey: ['me', 'status'],
    queryFn: () => api.get('/v1/users/me/status') as Promise<TotpStatus>,
    retry: false,
  });
  const [banner, setBanner] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('google_linked') === '1') {
      setBanner({ kind: 'ok', text: 'Google account linked. You can now Sign in with Google.' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (sp.has('google_error')) {
      const code = sp.get('google_error') ?? '';
      const text =
        code === 'email_mismatch'
          ? 'That Google account uses a different email than your XSC account. Sign in to Google with the address that matches your XSC email.'
          : code === 'already_linked_other'
            ? 'Your account is already linked to a different Google identity. Contact an administrator to reset it.'
            : `Could not link Google account (${code}).`;
      setBanner({ kind: 'error', text });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <section className="rounded border border-border bg-surface-alt p-5 space-y-3">
      <h2 className="text-lg font-medium">Sign in with Google</h2>
      <p className="text-sm text-ink-muted">
        Status: {data?.google_linked
          ? <span className="text-success">Linked</span>
          : <span>Not linked</span>}
      </p>
      {banner && (
        <p className={`text-sm ${banner.kind === 'ok' ? 'text-success' : 'text-danger'}`}>{banner.text}</p>
      )}
      {!data?.google_linked && (
        <>
          <p className="text-xs text-ink-muted">
            Link your Google account to your XSC user so you can sign in with the Google
            button on the login page. You must sign in to Google using the same email
            address as your XSC account.
          </p>
          <a
            href="/api/auth/oauth/google/start?intent=link"
            className="inline-block rounded bg-accent text-accent-fg px-4 py-2 text-sm font-medium"
          >
            Link my Google account
          </a>
        </>
      )}
    </section>
  );
}

interface GcalStatus {
  connected: boolean;
  google_email?: string;
  selected_calendar_id?: string | null;
  selected_calendar_summary?: string | null;
  push_channel_active?: boolean;
  push_expires_at?: string | null;
  last_synced_at?: string | null;
  last_error?: string | null;
}

interface GcalCalendar {
  id: string;
  summary: string;
  primary?: boolean;
  accessRole: string;
}

function GoogleCalendarCard(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<GcalStatus>({
    queryKey: ['me', 'google-calendar', 'status'],
    queryFn: () => api.get('/v1/me/google-calendar/status') as Promise<GcalStatus>,
    retry: false,
  });
  const [banner, setBanner] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [calendars, setCalendars] = useState<GcalCalendar[] | null>(null);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('gcal_connected') === '1') {
      setBanner({ kind: 'ok', text: 'Google Calendar connected. Pick which calendar to sync below.' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (sp.has('gcal_error')) {
      const code = sp.get('gcal_error') ?? '';
      const text =
        code === 'no_refresh_token'
          ? 'Google did not return a refresh token. Revoke access at myaccount.google.com → Security → Third-party apps, then try again.'
          : code === 'session_mismatch'
            ? 'Your sign-in changed mid-flow. Please retry.'
            : code === 'not_configured'
              ? 'Google Calendar sync is not configured on this server.'
              : `Could not connect Google Calendar (${code}).`;
      setBanner({ kind: 'error', text });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadCalendars = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = (await api.get('/v1/me/google-calendar/calendars')) as { calendars: GcalCalendar[] };
      setCalendars(res.calendars);
    } catch (err) {
      setBanner({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to list calendars' });
    } finally {
      setBusy(false);
    }
  };

  const pickCalendar = async (cal: GcalCalendar): Promise<void> => {
    setBusy(true);
    try {
      await api.put('/v1/me/google-calendar/selected', {
        calendar_id: cal.id,
        summary: cal.summary,
      });
      setBanner({ kind: 'ok', text: `Sync target set to "${cal.summary}".` });
      await qc.invalidateQueries({ queryKey: ['me', 'google-calendar', 'status'] });
      setCalendars(null);
    } catch (err) {
      setBanner({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to set calendar' });
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (): Promise<void> => {
    if (!window.confirm('Disconnect Google Calendar? Future events will no longer sync.')) return;
    setBusy(true);
    try {
      await api.post('/v1/me/google-calendar/disconnect', {});
      setBanner({ kind: 'ok', text: 'Disconnected.' });
      await qc.invalidateQueries({ queryKey: ['me', 'google-calendar', 'status'] });
    } catch (err) {
      setBanner({ kind: 'error', text: err instanceof Error ? err.message : 'Disconnect failed' });
    } finally {
      setBusy(false);
    }
  };

  const resync = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = (await api.post('/v1/me/google-calendar/resync', {})) as { processed: number };
      setBanner({ kind: 'ok', text: `Re-sync processed ${res.processed} pending operation(s).` });
    } catch (err) {
      setBanner({ kind: 'error', text: err instanceof Error ? err.message : 'Re-sync failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded border border-border bg-surface-alt p-5 space-y-3">
      <h2 className="text-lg font-medium">Google Calendar sync</h2>
      <p className="text-sm text-ink-muted">
        Two-way sync between your XSC events and a Google Calendar of your choice.
        Changes made in either system are mirrored to the other within ~30 seconds.
      </p>
      {banner && (
        <p className={`text-sm ${banner.kind === 'ok' ? 'text-success' : 'text-danger'}`}>{banner.text}</p>
      )}
      {isLoading ? (
        <p className="text-sm text-ink-muted">Loading…</p>
      ) : !data?.connected ? (
        <a
          href="/v1/me/google-calendar/connect"
          className="inline-block rounded bg-accent text-accent-fg px-4 py-2 text-sm font-medium"
        >
          Connect Google Calendar
        </a>
      ) : (
        <div className="space-y-3">
          <dl className="text-sm space-y-1">
            <div className="flex gap-2">
              <dt className="text-ink-muted">Google account:</dt>
              <dd>{data.google_email}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-ink-muted">Selected calendar:</dt>
              <dd>
                {data.selected_calendar_summary ?? data.selected_calendar_id ?? (
                  <span className="text-warning">not set</span>
                )}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-ink-muted">Push channel:</dt>
              <dd>
                {data.push_channel_active ? (
                  <span className="text-success">active</span>
                ) : (
                  <span className="text-ink-muted">inactive</span>
                )}
                {data.push_expires_at && (
                  <span className="text-ink-muted"> (expires {new Date(data.push_expires_at).toLocaleString()})</span>
                )}
              </dd>
            </div>
            {data.last_synced_at && (
              <div className="flex gap-2">
                <dt className="text-ink-muted">Last sync:</dt>
                <dd>{new Date(data.last_synced_at).toLocaleString()}</dd>
              </div>
            )}
            {data.last_error && (
              <div className="flex gap-2">
                <dt className="text-ink-muted">Last error:</dt>
                <dd className="text-danger">{data.last_error}</dd>
              </div>
            )}
          </dl>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              className="rounded border border-border px-3 py-1.5 text-sm"
              onClick={() => void loadCalendars()}
            >
              {data.selected_calendar_id ? 'Change calendar…' : 'Pick a calendar…'}
            </button>
            <button
              type="button"
              disabled={busy}
              className="rounded border border-border px-3 py-1.5 text-sm"
              onClick={() => void resync()}
            >
              Re-sync now
            </button>
            <button
              type="button"
              disabled={busy}
              className="rounded border border-danger text-danger px-3 py-1.5 text-sm"
              onClick={() => void disconnect()}
            >
              Disconnect
            </button>
          </div>
          {calendars && (
            <ul className="border border-border rounded divide-y divide-border max-h-64 overflow-y-auto">
              {calendars.map((c) => (
                <li
                  key={c.id}
                  className="flex justify-between items-center p-2 text-sm hover:bg-surface cursor-pointer"
                  onClick={() => void pickCalendar(c)}
                >
                  <span>
                    {c.summary} {c.primary && <span className="text-ink-muted">(primary)</span>}
                  </span>
                  <span className="text-xs text-ink-muted">{c.accessRole}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function ChangePasswordCard(): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setOk(false);
    if (next.length < 8) { setError('New password must be 8+ characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/v1/users/me/password', { current_password: current, new_password: next });
      setOk(true);
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setError('Current password is incorrect');
      else setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded border border-border bg-surface-alt p-5 space-y-3">
      <h2 className="text-lg font-medium">Change password</h2>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Current password">
          <input type="password" className={inputCls} autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="New password" hint="8+ characters">
          <input type="password" className={inputCls} autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="Confirm new password">
          <input type="password" className={inputCls} autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        {error && <p className="text-sm text-danger">{error}</p>}
        {ok && <p className="text-sm text-success">Password updated.</p>}
        <button type="submit" disabled={busy}
          className="rounded bg-accent text-accent-fg px-4 py-2 text-sm font-medium disabled:opacity-60">
          {busy ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </section>
  );
}

function TwoFactorCard(): JSX.Element {
  const qc = useQueryClient();
  const { data, refetch } = useQuery<TotpStatus>({
    queryKey: ['me', 'status'],
    queryFn: () => api.get('/v1/users/me/status') as Promise<TotpStatus>,
    retry: false,
  });
  const [setup, setSetup] = useState<{ secret: string; otpauth_url: string } | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disablePw, setDisablePw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const startEnrol = async (): Promise<void> => {
    setError(null); setInfo(null);
    try {
      const r = (await api.get('/v1/users/me/totp/setup')) as { secret: string; otpauth_url: string };
      setSetup(r);
    } catch (err) { setError((err as Error).message); }
  };

  const confirmEnrol = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!setup || !/^\d{6}$/.test(enrollCode)) { setError('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      await api.post('/v1/users/me/totp/enable', { secret: setup.secret, code: enrollCode });
      setSetup(null); setEnrollCode(''); setInfo('Two-factor enabled.');
      qc.invalidateQueries({ queryKey: ['me', 'status'] });
      void refetch();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? 'Code did not match' : (err as Error).message);
    } finally { setBusy(false); }
  };

  const disable = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/v1/users/me/totp/disable', {
        current_password: disablePw,
        ...(disableCode ? { code: disableCode } : {}),
      });
      setDisableCode(''); setDisablePw(''); setInfo('Two-factor disabled.');
      qc.invalidateQueries({ queryKey: ['me', 'status'] });
      void refetch();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? 'Password or code is wrong' : (err as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded border border-border bg-surface-alt p-5 space-y-3">
      <h2 className="text-lg font-medium">Two-factor authentication</h2>
      <p className="text-sm text-ink-muted">
        Status: {data?.totp_enabled
          ? <span className="text-success">Enabled</span>
          : <span>Not enabled</span>}
      </p>
      {error && <p className="text-sm text-danger">{error}</p>}
      {info && <p className="text-sm text-success">{info}</p>}

      {!data?.totp_enabled && !setup && (
        <button onClick={() => void startEnrol()}
          className="rounded bg-accent text-accent-fg px-4 py-2 text-sm font-medium">
          Enable 2FA
        </button>
      )}

      {setup && (
        <div className="space-y-3">
          <div className="rounded border border-border bg-surface p-3 space-y-2">
            <p className="text-sm">Scan this with Google Authenticator, 1Password, Authy, or any TOTP app:</p>
            <img alt="TOTP QR code"
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setup.otpauth_url)}`}
              className="border border-border bg-white p-2 rounded"
              width={200} height={200} />
            <details>
              <summary className="text-xs text-ink-muted cursor-pointer">Can't scan? Show secret</summary>
              <code className="block mt-1 text-xs font-mono break-all">{setup.secret}</code>
            </details>
          </div>
          <form onSubmit={confirmEnrol} className="space-y-2">
            <Field label="Enter the 6-digit code to confirm">
              <input type="text" inputMode="numeric" maxLength={6} className={inputCls}
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ''))} />
            </Field>
            <div className="flex gap-2">
              <button type="submit" disabled={busy}
                className="rounded bg-accent text-accent-fg px-4 py-2 text-sm font-medium disabled:opacity-60">
                {busy ? 'Verifying…' : 'Confirm and enable'}
              </button>
              <button type="button" onClick={() => { setSetup(null); setEnrollCode(''); }}
                className="rounded bg-surface px-4 py-2 text-sm border border-border">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {data?.totp_enabled && (
        <form onSubmit={disable} className="space-y-3 border-t border-border pt-3">
          <p className="text-sm font-medium">Disable two-factor</p>
          <Field label="Current password">
            <input type="password" className={inputCls} autoComplete="current-password"
              value={disablePw} onChange={(e) => setDisablePw(e.target.value)} />
          </Field>
          <Field label="Current 6-digit code (optional but recommended)">
            <input type="text" inputMode="numeric" maxLength={6} className={inputCls}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <button type="submit" disabled={busy}
            className="rounded bg-danger text-accent-fg px-4 py-2 text-sm font-medium disabled:opacity-60">
            {busy ? 'Disabling…' : 'Disable 2FA'}
          </button>
        </form>
      )}
    </section>
  );
}

const inputCls = 'w-full rounded border border-border bg-surface px-2 py-1 text-sm';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <div className="text-sm font-medium">{label}</div>
      {hint && <div className="text-xs text-ink-muted mb-1">{hint}</div>}
      {children}
    </label>
  );
}
